import { FC, useEffect, useMemo, useState } from 'react';
import { IBaseAudienceStepProps } from './types';
import { CampaignStepHeader, CampaignStepTitle } from '../../../common';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { CreateCampaignFormInput } from '../../../../validation';
import { useWorkspace } from '@/features/workspaces/hooks';
import { DestinationItemSetup } from './components';
import { CampaignContent, CampaignDestinationItem } from './components/types';
import { DistributionChannelConnector, DistributionChannelContent, DistributionChannelParameterUpdate } from '@/models/distributionChannels';
import { useUpdateDistributionChannelParametersMutation } from '@/services/endpoints/distributionChannels';
import { useTranslation } from 'react-i18next';
import { AudienceConnectorsLinkMessage } from '@/features/audiences';

export const SetupDestinationConnection: FC<IBaseAudienceStepProps> = ({ toNextStep }) => {
  const { t } = useTranslation();
  const workspace = useWorkspace();
  const { getValues, watch, control } = useFormContext<CreateCampaignFormInput>();
  const { update: updateConnectors } = useFieldArray({
    control,
    name: 'connectors',
  });

  const audience = getValues('audience');
  const connectors = watch('connectors');

  const content = useMemo<CampaignContent[]>(
    () =>
      connectors.map(({ connector, distributionChannelContent }) => ({
        connector,
        distributionChannelContent,
      })),
    [connectors]
  );

  const [intermediateData, setIntermediateData] = useState<CampaignDestinationItem[]>(
    content.map((data) => ({
      ...data,
      selectedParameters: {},
    }))
  );

  const [isDestinationLoading, setIsDistributionChannelLoading] = useState<boolean>(true);
  const handlesetIsDistributionChannelLoading = (loading: boolean) => {
    setIsDistributionChannelLoading(loading);
  };

  const handleSetDistributionChannelContent = (connector: DistributionChannelConnector, content: DistributionChannelContent) => {
    setIntermediateData((prev) => {
      const newData = prev.map((oldItem) => {
        if (oldItem.connector.name === connector.name) {
          return {
            ...oldItem,
            distributionChannelContent: content,
          };
        } else {
          return oldItem;
        }
      });

      return newData;
    });
  };

  const handleSetParameter = (connector: DistributionChannelConnector, update: DistributionChannelParameterUpdate) => {
    setIntermediateData((prev) => {
      const newData = prev.map((oldItem) => {
        if (oldItem.connector.name === connector.name) {
          return {
            ...oldItem,
            selectedParameters: { ...oldItem.selectedParameters, [update.parameterId]: update },
            isParametersUpdated: false,
          };
        } else {
          return oldItem;
        }
      });

      return newData;
    });
  };

  const handleSetIsParametersUpdated = (connector: DistributionChannelConnector) => {
    setIntermediateData((prev) => {
      const newData = prev.map((oldItem) => {
        if (oldItem.connector.name === connector.name) {
          return {
            ...oldItem,
            isParametersUpdated: true,
          };
        } else {
          return oldItem;
        }
      });

      return newData;
    });
  };

  const [updateDistributionChannelParameters, { isLoading: isUpdateParametersLoading }] = useUpdateDistributionChannelParametersMutation();

  const handleClickNextStep = async () => {
    const promises = intermediateData
      .filter((item) => !item.isParametersUpdated)
      .map(async (item) => {
        const result = await updateDistributionChannelParameters({
          workspaceId: workspace.id,
          audienceId: audience.id,
          distributionChannelId: item.distributionChannelContent?.distributionChannel.id as string,
          parameters: Object.values(item.selectedParameters),
        });

        if ('data' in result) {
          handleSetDistributionChannelContent(item.connector, result.data as unknown as DistributionChannelContent);
          handleSetIsParametersUpdated(item.connector);
        }
      });

    await Promise.all(promises);
  };

  const isAllParametersUpdated = intermediateData.every((item) => item.isParametersUpdated);

  const isNextButtonDisabled =
    intermediateData.some(
      (x) =>
        !!x.distributionChannelContent?.integrationsResult.cyclr?.action?.parameters
          ?.filter((param) => !param.isOptional)
          .find((param) => !x.selectedParameters[param.parameterId] || !x.selectedParameters[param.parameterId].value)
    ) || isAllParametersUpdated;

  useEffect(() => {
    if (isAllParametersUpdated) {
      const setuppedDestination = intermediateData.map((item) => {
        return {
          connector: item.connector,
          distributionChannelContent: item.distributionChannelContent,
        } satisfies CampaignContent;
      });

      connectors.forEach((c, i) => {
        const connector = setuppedDestination.find((item) => item.connector.name === c.connector.name);

        updateConnectors(i, {
          ...connector,
          distributionChannelContent: connector.distributionChannelContent,
        });
      });

      toNextStep();
    }
  }, [intermediateData, isAllParametersUpdated]);

  return (
    <div className='flex flex-col gap-12'>
      <CampaignStepHeader
        primaryButton={{
          text: t('COMMON:confirm'),
          onClick: handleClickNextStep,
          disabled: isDestinationLoading || isNextButtonDisabled || isUpdateParametersLoading,
          isLoading: isUpdateParametersLoading || isAllParametersUpdated,
        }}
      >
        <CampaignStepTitle
          classNames={{ title: 'first-line:text-primary-900' }}
          title='whichResourcesYouWantToPopulate_title'
          description='whichResourcesYouWantToPopulate_description'
        />
      </CampaignStepHeader>
      <div className='flex max-w-screen-lg flex-col'>
        <AudienceConnectorsLinkMessage audienceName={audience.name} connectors={content} />
        <div className='flex grow flex-col divide-y divide-quaternary-200'>
          {intermediateData.map((data) => (
            <DestinationItemSetup
              key={data.connector.name}
              audience={audience}
              setDistributionChannelContent={handleSetDistributionChannelContent}
              data={data}
              parameterValueChange={handleSetParameter}
              setIsDistributionChannelLoading={handlesetIsDistributionChannelLoading}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
